/**
 * card-generator.js — turn any image bubble into a shareable "one-tap card".
 *
 * Given a base64 or data-URL image + a caption (the generation/edit prompt),
 * renders a 1200×675 JPEG poster with:
 *   • the image on the left, contained inside a rounded frame with a soft cyan halo
 *   • a warm caption block on the right ("Made with Rammy" mark + the prompt quote)
 *
 * Then either fires `navigator.share({files})` on supported clients, or falls
 * back to a direct download. Fully client-side — no server round-trip, no
 * dependencies beyond the canvas 2D API.
 */
const CANVAS_W = 1200;
const CANVAS_H = 675;

// Load an <img> from a data URL / blob URL / raw base64 string.
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // We only render our own images sourced from the API, so cross-origin
    // shouldn't apply — but declare it anyway to be safe.
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src.startsWith("data:") || src.startsWith("blob:") || src.startsWith("http")
      ? src
      : `data:image/png;base64,${src}`;
  });
}

// Rounded-rect helper (canvas 2D still doesn't ship one everywhere).
function roundRectPath(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
}

// Word-wrap text to fit a max width. Returns lines[].
function wrapText(ctx, text, maxWidth, maxLines = 6) {
  const words = (text || "").trim().split(/\s+/);
  const lines = [];
  let line = "";
  for (const w of words) {
    const candidate = line ? line + " " + w : w;
    if (ctx.measureText(candidate).width > maxWidth && line) {
      lines.push(line);
      line = w;
      if (lines.length === maxLines - 1) break;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  // Truncate the final line with an ellipsis if we still have words left.
  if (lines.length === maxLines) {
    let last = lines[maxLines - 1];
    while (ctx.measureText(last + "…").width > maxWidth && last.length > 1) {
      last = last.slice(0, -1);
    }
    lines[maxLines - 1] = last + "…";
  }
  return lines;
}

async function renderCard({ imageSrc, caption, robotName = "Rammy" }) {
  const canvas = document.createElement("canvas");
  canvas.width = CANVAS_W;
  canvas.height = CANVAS_H;
  const ctx = canvas.getContext("2d");

  // Warm cream backdrop with a whisper of cyan on the right edge.
  const bg = ctx.createLinearGradient(0, 0, CANVAS_W, 0);
  bg.addColorStop(0, "#faf7f2");
  bg.addColorStop(1, "#eef7f4");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

  // Subtle grain (composited via drawImage so it multiplies with the bg).
  {
    const grainC = document.createElement("canvas");
    grainC.width = CANVAS_W; grainC.height = CANVAS_H;
    const gctx = grainC.getContext("2d");
    const img = gctx.createImageData(CANVAS_W, CANVAS_H);
    for (let i = 0; i < img.data.length; i += 4) {
      const n = (Math.random() * 255) | 0;
      img.data[i] = img.data[i + 1] = img.data[i + 2] = n;
      img.data[i + 3] = 255;
    }
    gctx.putImageData(img, 0, 0);
    ctx.save();
    ctx.globalAlpha = 0.022;
    ctx.globalCompositeOperation = "multiply";
    ctx.drawImage(grainC, 0, 0);
    ctx.restore();
  }

  // ── Left column: image ─────────────────────────────────────────
  const padL = 44, padT = 44, padB = 44;
  const boxW = 600, boxH = CANVAS_H - padT - padB; // 587
  const boxX = padL, boxY = padT;

  // Cyan halo behind the image (radial glow).
  const halo = ctx.createRadialGradient(
    boxX + boxW / 2, boxY + boxH / 2, boxW * 0.35,
    boxX + boxW / 2, boxY + boxH / 2, boxW * 0.8,
  );
  halo.addColorStop(0, "rgba(72,224,200,0.28)");
  halo.addColorStop(1, "rgba(72,224,200,0)");
  ctx.fillStyle = halo;
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

  // Rounded frame with a soft shadow.
  ctx.save();
  ctx.shadowColor = "rgba(17,19,24,0.18)";
  ctx.shadowBlur = 30;
  ctx.shadowOffsetY = 10;
  roundRectPath(ctx, boxX, boxY, boxW, boxH, 28);
  ctx.fillStyle = "#0f1218";
  ctx.fill();
  ctx.restore();

  // Draw the image (contain-fit inside the frame with a small inner inset).
  try {
    const img = await loadImage(imageSrc);
    const inset = 10;
    const innerX = boxX + inset, innerY = boxY + inset;
    const innerW = boxW - inset * 2, innerH = boxH - inset * 2;
    const scale = Math.min(innerW / img.width, innerH / img.height);
    const dw = img.width * scale, dh = img.height * scale;
    const dx = innerX + (innerW - dw) / 2, dy = innerY + (innerH - dh) / 2;
    ctx.save();
    roundRectPath(ctx, boxX + 4, boxY + 4, boxW - 8, boxH - 8, 22);
    ctx.clip();
    ctx.drawImage(img, dx, dy, dw, dh);
    ctx.restore();
  } catch (e) {
    console.warn("card: image load failed", e);
    ctx.fillStyle = "#faf7f2";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = "italic 20px 'Playfair Display', Georgia, serif";
    ctx.fillText("(image unavailable)", boxX + boxW / 2, boxY + boxH / 2);
  }

  // ── Right column: caption ──────────────────────────────────────
  const rightX = padL + boxW + 44;
  const rightW = CANVAS_W - rightX - padL;

  // Small caps eyebrow.
  ctx.fillStyle = "#7d8497";
  ctx.font = "600 13px 'Manrope', system-ui, sans-serif";
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  const eyebrow = "MADE WITH";
  ctx.fillText(eyebrow, rightX, padT + 30);
  // Letter-spacing simulated with a manual redraw isn't worth it — the label
  // reads fine at this size.

  // Wordmark.
  ctx.fillStyle = "#111318";
  ctx.font = "700 76px 'Playfair Display', Georgia, serif";
  ctx.fillText(robotName, rightX, padT + 108);

  // Cyan underline flourish.
  ctx.fillStyle = "#48e0c8";
  ctx.fillRect(rightX, padT + 128, 64, 4);

  // Caption body — the actual prompt, in serif italic quotes.
  const captionText = (caption || "A moment made with Rammy.").trim();
  ctx.fillStyle = "#2b2f3a";
  ctx.font = "italic 30px 'Playfair Display', Georgia, serif";
  ctx.textBaseline = "top";
  const captionY = padT + 172;
  const lineHeight = 42;
  const maxLines = 7;
  const lines = wrapText(ctx, `"${captionText}"`, rightW, maxLines);
  lines.forEach((line, i) => {
    ctx.fillText(line, rightX, captionY + i * lineHeight);
  });

  // Footer: rammy.ai
  ctx.fillStyle = "#7d8497";
  ctx.font = "500 15px 'Manrope', system-ui, sans-serif";
  ctx.textBaseline = "bottom";
  ctx.fillText("rammy.ai", rightX, CANVAS_H - padB);

  // Small cyan dot next to footer as a mini brand mark.
  ctx.fillStyle = "#48e0c8";
  ctx.beginPath();
  ctx.arc(rightX - 12, CANVAS_H - padB - 6, 4, 0, Math.PI * 2);
  ctx.fill();

  return await new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.92);
  });
}

/**
 * The public entry point. Returns { poster: Blob, share: () => Promise }.
 */
export async function createOneTapCard({ imageSrc, caption, robotName = "Rammy" }) {
  const poster = await renderCard({ imageSrc, caption, robotName });

  const share = async () => {
    if (!poster) return "failed";
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    const file = new File([poster], `rammy-card-${stamp}.jpg`, { type: "image/jpeg" });

    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          title: `Made with ${robotName}`,
          text: caption ? `"${caption}" — made with ${robotName}` : `Made with ${robotName}`,
          files: [file],
        });
        return "shared";
      } catch (e) {
        if (e?.name === "AbortError") return "cancelled";
        // fall through to download
      }
    }

    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
    return "download";
  };

  return { poster, share };
}
